import React, { createContext, useContext, useEffect, useState } from 'react';
import { ExpandMore as ExpandMoreIcon, Link as LinkIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link as MuiLink,
  List as MuiList,
  ListItemText,
  ListProps,
  Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { APP_HEADER_HEIGHT_SM, APP_HEADER_HEIGHT_XS, TABS_BAR_HEIGHT } from '../../utils';

// Derive a stable URL slug from heading text, so every section is deep-linkable without call sites
// passing an id. Deterministic: the slug IS the shareable anchor, so renaming a heading changes its
// link. e.g. "Postseason Dividends" -> "postseason-dividends".
function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

// The single source of truth for the main sections, in render order. Both the Table of Contents and
// each section's heading + "in short" summary read from here, so a section's title and description can
// never drift between the two. A section added below is added here once to become fully navigable.
const SECTIONS: { title: string; summary: string }[] = [
  {
    title: 'General Rules',
    summary: 'The basics — how shares, prices, and the weekly market schedule work.',
  },
  {
    title: 'Preseason Auction',
    summary:
      'Teams are sold in an opening auction before the season. Bid on the teams you want; unsold shares stay with the bank at their initial price.',
  },
  {
    title: 'Regular Season Trades',
    summary: "While the market's open, buy and sell shares with other players like a live exchange.",
  },
  {
    title: 'Short Selling',
    summary: 'Write a new share, sell it to someone else, and get paid when the team loses.',
  },
  {
    title: 'Regular Season Secondary Auctions',
    summary: "A few new shares can be issued each week, but only once a team's bank shares have run out.",
  },
  {
    title: 'Market-Opening Reconciliation',
    summary:
      'How all waiting orders for a team settle together at one fair price when the market reopens, instead of first-come-first-served.',
  },
  {
    title: 'Regular Season Dividends',
    summary: 'Every regular season game pays players who own shares of the winner, and anyone who shorted the loser.',
  },
  {
    title: 'Postseason Dividends',
    summary:
      'Championships, bowls, and playoff runs pay fixed cash bonuses per share owned, and charge penalties to anyone short on the team.',
  },
  {
    title: 'Payout Structure',
    summary: "At season's end, everyone above the league's average portfolio value splits the prize pool.",
  },
  {
    title: 'Sportsmanship and Game Integrity',
    summary: 'Play in good faith. The commissioner has final say and broad authority to keep the game fair.',
  },
];

function BulletList({ children, sx, ...rest }: ListProps) {
  return (
    <MuiList
      dense
      disablePadding
      sx={{
        listStyle: 'disc',
        pl: 4,
        mt: 0.5,
        '& > .MuiListItemText-root': {
          display: 'list-item',
          mt: 0.75,
          pr: 0,
        },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </MuiList>
  );
}

// Whether the page this is rendered into has a sticky league tab bar (see useTabs's `sticky` option)
// stacked below the app bar. False by default (matching the public rules page, which has no such tab
// bar); the in-league tab overrides this to true, since it renders below the league's sticky tabs. Read
// by LinkableHeading, which is nested many levels below the top-level component's `stickyTabBar` prop --
// a context avoids threading that prop through every RuleSection/SubHeading call site.
const StickyTabBarContext = createContext(false);

// A heading you can link straight to. The heading is the anchor target (id = slug); hovering reveals a
// # icon whose href is `#slug`, so a click drops the anchor into the address bar to copy and share.
// scrollMarginTop keeps the target clear of the sticky app bar and, where present, the sticky league
// tab bar rendered above this page on deep-link scroll.
function LinkableHeading({
  id,
  variant,
  sx,
  children,
}: {
  id: string;
  variant: 'h6' | 'subtitle1';
  sx?: object;
  children: React.ReactNode;
}) {
  const stickyTabBar = useContext(StickyTabBarContext);
  const tabsBarOffset = stickyTabBar ? TABS_BAR_HEIGHT : 0;
  return (
    <Typography
      id={id}
      variant={variant}
      sx={{
        scrollMarginTop: {
          xs: APP_HEADER_HEIGHT_XS + tabsBarOffset + 16,
          sm: APP_HEADER_HEIGHT_SM + tabsBarOffset + 16,
        },
        fontWeight: variant === 'subtitle1' ? 'bold' : undefined,
        '& .anchor-link': { opacity: 0, transition: 'opacity 0.15s' },
        '&:hover .anchor-link, & .anchor-link:focus-visible': { opacity: 1 },
        ...sx,
      }}
    >
      {children}
      <MuiLink
        href={`#${id}`}
        className="anchor-link"
        aria-label="Link to this section"
        sx={{ ml: 1, verticalAlign: 'middle', color: 'text.secondary', lineHeight: 0 }}
      >
        <LinkIcon fontSize="small" />
      </MuiLink>
    </Typography>
  );
}

// The heading style used atop each of the page's boxed orientation elements (Overview, Contents,
// What Changed), so they read as one family of "read this before the rules" callouts rather than
// three differently-styled pieces of intro content.
function BoxLabel({ children }: { children: React.ReactNode }) {
  return <Typography variant="h6">{children}</Typography>;
}

// One plain-language sentence per section: read this, or read the fine print below it.
function SectionSummary({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
      {children}
    </Typography>
  );
}

// A tinted, accented box for a worked example, so it reads as "here's a concrete case" instead of more
// prose — which is exactly what people skim these rules for.
function ExampleCallout({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderLeft: 3,
        borderColor: 'primary.main',
        bgcolor: 'action.hover',
        borderRadius: 1,
      }}
    >
      <Typography variant="overline" color="primary" sx={{ display: 'block', lineHeight: 1.5 }}>
        Example
      </Typography>
      {children}
    </Box>
  );
}

// One section. Its title-derived slug is the anchor target, and its number + "in short" summary are
// sourced from SECTIONS by position, so the number and description always match the Table of Contents.
// The number is display-only — the slug stays the plain title, so anchor links survive a reordering.
function RuleSection({ title, children }: { title: string; children: React.ReactNode }) {
  const id = slugify(title);
  const index = SECTIONS.findIndex(section => section.title === title);
  const summary = index >= 0 ? SECTIONS[index].summary : undefined;
  return (
    <Box component="section" sx={{ mt: 3 }}>
      <LinkableHeading id={id} variant="h6">
        {index + 1}. {title}
      </LinkableHeading>
      {summary && <SectionSummary>{summary}</SectionSummary>}
      {children}
    </Box>
  );
}

function SubHeading({ children }: { children: string }) {
  return (
    <LinkableHeading id={slugify(children)} variant="subtitle1" sx={{ mt: 2 }}>
      {children}
    </LinkableHeading>
  );
}

// One friendly, plain-language change, for players who played last year's Stock Market Simulation
// league and want to know what's actually different before they wade into the rules below.
const CHANGES_FROM_LAST_YEAR: { title: string; body: React.ReactNode }[] = [
  {
    title: 'A real preseason auction',
    body: 'Last year you just bought shares at a fixed price before a deadline. This year, teams are auctioned off to the highest bidders — expensive teams go first, so spending big early can leave you short on cash for teams you want later.',
  },
  {
    title: 'A limited, fixed number of shares per team',
    body: "Each team only has so many shares, based on league size. You're trading pieces of a fixed pie, not buying from a bottomless supply — once they're gone, you need another player willing to sell, or wait for a weekly secondary auction.",
  },
  {
    title: 'One trading price, not separate buy and sell prices',
    body: "Last year, buying and selling a team had different prices — a built-in spread. This year every team has a single share value that moves only when real trades happen at real prices, and that's the number everyone buys and sells around.",
  },
  {
    title: 'Shorting is back, reworked',
    body: 'No more borrowing or formula-based pricing. Now you write a real share and sell it to someone else to own outright. See Short Selling below for the full mechanics.',
  },
  {
    title: 'Shorts receive dividends in the regular season, and pay penalties in the postseason',
    body: "New this year, when you are short on a team, you will receive a dividend when that team loses a regular season game. The postseason is different: owners are paid per share for championships, bowls and playoff runs, and anyone short that team will be charged the same amount as a penalty (negative dividend). So a short you're still holding when the market closes for the last time is a real bet that the team does nothing in the postseason.",
  },
  {
    title: 'Bigger, more predictable dividends',
    body: "Regular season win dividends are now 20% of the losing team's value per share, up from 15%. Postseason bonuses — conference titles, bowl wins, playoff runs — now pay flat dollar amounts per share instead of a percentage, so you know exactly what a championship is worth going in.",
  },
  {
    title: 'Weekly orders settle together, fairly',
    body: "When the market reopens each week, everyone's waiting buy and sell orders clear together at one fair price, instead of whoever clicked first getting the best deal.",
  },
];

const CHANGELOG_EXPANDED_STORAGE_KEY = 'stockMarketRulesChangelogExpanded:2026';

// The summary a returning player skims to build a mental model before hitting the precise rules
// below. Open by default, but remembers this device's last choice so a player who reads it once
// doesn't have to re-expand it every visit.
function WhatChangedCard() {
  const [expanded, setExpanded] = useState(() => {
    const stored = localStorage.getItem(CHANGELOG_EXPANDED_STORAGE_KEY);
    return stored === null ? true : stored === '1';
  });

  return (
    <Accordion
      expanded={expanded}
      onChange={(_event, isExpanded) => {
        setExpanded(isExpanded);
        localStorage.setItem(CHANGELOG_EXPANDED_STORAGE_KEY, isExpanded ? '1' : '0');
      }}
      elevation={0}
      sx={{
        mt: 2,
        bgcolor: 'action.hover',
        backgroundImage: 'none',
        '&:before': { display: 'none' },
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        '&.MuiAccordion-root': { borderRadius: 1 },
        '&.MuiAccordion-root.Mui-expanded:first-of-type': { mt: 2 },
        '& .MuiAccordionSummary-root.Mui-expanded': { minHeight: 48 },
        '& .MuiAccordionSummary-content.Mui-expanded': { my: 1.5 },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <BoxLabel>What changed from last year?</BoxLabel>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="body1">
          Last year we ran this as the Stock Market Simulation league. This year&apos;s Stock Market league keeps the
          same core idea — buy and sell shares of college football teams, earn dividends when they win — but works
          differently under the hood. Here&apos;s the short version, in plain terms:
        </Typography>
        <BulletList>
          {CHANGES_FROM_LAST_YEAR.map(({ title, body }) => (
            <ListItemText key={title}>
              <strong>{title}.</strong> {body}
            </ListItemText>
          ))}
        </BulletList>
        <Typography variant="body1" sx={{ mt: 2 }}>
          You&apos;ll also start with more cash to invest — $2,000 instead of $1,500. The rest of this page covers the
          full rules in detail, including how all of this works under the hood.
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

// Boxed and labeled the same way as Contents below it, so its purpose — the fast version, full detail
// further down — reads as deliberate rather than as throwaway intro prose.
function Tldr() {
  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
      <BoxLabel>Overview</BoxLabel>
      <Typography variant="body1" sx={{ mt: 1 }}>
        You&apos;re a trader in a stock market where the shares are college football teams. You start with a fixed
        amount of play money, and the goal is simple: finish the season with a more valuable portfolio than your
        opposing players. Here&apos;s the shape of a season:
      </Typography>
      <BulletList>
        <ListItemText>
          <strong>Buy in.</strong> Everyone pays a $20 entry fee into a shared prize pool and receives play money to
          invest.
        </ListItemText>
        <ListItemText>
          <strong>Stock up in the preseason auction.</strong> Before any games are played, each team&apos;s shares are
          sold in an opening auction — your first chance to load up on the teams you believe in.
        </ListItemText>
        <ListItemText>
          <strong>Trade each week.</strong> Once the season starts the market opens weekly, so you can buy and sell
          shares with other players as teams rise and fall.
        </ListItemText>
        <ListItemText>
          <strong>Short sell teams you&apos;re down on.</strong> Sell a share you don&apos;t own, and profit when the
          team loses.
        </ListItemText>
        <ListItemText>
          <strong>Collect dividends.</strong> Your owned teams pay you cash when they win games and earn postseason
          accolades. Your shorts pay you when the team loses, but can cost big if you carry shorts into the postseason.
        </ListItemText>
        <ListItemText>
          <strong>Beat the average.</strong> At season&apos;s end, everyone above the league&apos;s average portfolio
          value splits the prize pool — what matters is how you do relative to everyone else, not your raw gain.
        </ListItemText>
      </BulletList>
    </Box>
  );
}

// Doubles as an index and a finder: each entry is a jump link plus the section's description, so a
// reader can scan the summaries to figure out which section answers their question before diving in.
function TableOfContents() {
  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
      <BoxLabel>Contents</BoxLabel>
      <Box component="ul" sx={{ columns: { xs: 1, md: 2 }, listStyle: 'none', p: 0, m: 0, mt: 1 }}>
        {SECTIONS.map(({ title, summary }, index) => (
          <Box component="li" key={title} sx={{ breakInside: 'avoid', mb: 1.5 }}>
            <MuiLink href={`#${slugify(title)}`} underline="hover" sx={{ fontWeight: 500 }}>
              {index + 1}. {title}
            </MuiLink>
            <Typography variant="body2" color="text.secondary">
              {summary}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function StockMarketRules({
  title = 'League Rules',
  headerAction,
  stickyTabBar = false,
}: {
  title?: string;
  headerAction?: React.ReactNode;
  // The in-league tab renders through useTabs, which passes { leagueId } to every tab's Component --
  // unused here, but must be accepted so this stays assignable alongside the other tabs.
  leagueId?: string;
  // See StickyTabBarContext above. Defaults to false (matching the public rules page, which has no
  // sticky tab bar to clear); the in-league tab overrides this to true via a wrapper component.
  stickyTabBar?: boolean;
}) {
  // Deep links (/league/:id/rules#payout-structure) can't rely on the browser's native anchor scroll:
  // when the URL is first read the tab's content hasn't mounted, so the target id doesn't exist yet.
  // Scroll to it ourselves once rendered, and again whenever the hash changes (Contents / anchor click).
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <StickyTabBarContext.Provider value={stickyTabBar}>
      <Box sx={{ maxWidth: 1000, width: '100%', mt: 2, mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">{title}</Typography>
          {headerAction}
        </Box>

        <Tldr />

        <WhatChangedCard />

        <TableOfContents />

        <RuleSection title="General Rules">
          <BulletList>
            <ListItemText>Each player starts the season with $2,000 in cash to bid and trade with.</ListItemText>
          </BulletList>
          <BulletList>
            <ListItemText>
              Each team starts the season with the same fixed number of issued shares, based on league size: given N
              players, share count is N rounded up to a multiple of 5 (a 15-player league issues 15, not 20).
            </ListItemText>
          </BulletList>
          <ExampleCallout>
            <Typography variant="body2">A 12-player league issues 15 shares of each team.</Typography>
          </ExampleCallout>
          <BulletList>
            <ListItemText>
              Each team has a calculated initial price, used by the bank and the preseason auction.
            </ListItemText>
            <ListItemText>
              A team&apos;s share value is an exponential moving average of its recent trade prices. Each share traded
              nudges the share value 20% of the way toward that trade&apos;s price, so moving it meaningfully takes real
              volume at real prices. Before any trades, the share value is the team&apos;s auction clearing price, or
              its initial price if the auction sold no shares.
            </ListItemText>
          </BulletList>
          <ExampleCallout>
            <Typography variant="body2">
              A team&apos;s share value is $20 and one share trades at $30. The new share value moves 20% of the way
              there: (80% × $20) + (20% × $30) = $24. It would take several more trades near $30 to pull the value close
              to $30.
            </Typography>
          </ExampleCallout>
          <BulletList>
            <ListItemText>
              A player&apos;s portfolio value for a team is their shares of that team times its share value. Their total
              portfolio value is the sum of that across all teams, plus their cash balance.
            </ListItemText>
          </BulletList>
          <ExampleCallout>
            <Typography variant="body2">
              Holding 4 shares of a $30 team, 2 shares of a $50 team, and $200 in cash is a portfolio value of (4 × $30)
              + (2 × $50) + $200 = $120 + $100 + $200 = $420.
            </Typography>
          </ExampleCallout>
          <BulletList>
            <ListItemText>
              The market is open each week from Monday 12:00 pm ET through Saturday 12:00 pm ET; trades process only
              while it&apos;s open. Orders can be placed anytime and process the next time the market opens.
              <BulletList sx={{ listStyle: 'circle' }}>
                <ListItemText>
                  The season&apos;s first opening is an exception: instead of a set Monday, it opens as soon as the
                  preseason auction concludes, then stays open through the Saturday close of week 1.
                </ListItemText>
                <ListItemText>
                  On Labor Day Weekend, the market opens Tuesday instead of Monday, since there&apos;s a game Monday
                  night.
                </ListItemText>
                <ListItemText>
                  If a team&apos;s game kicks off before Saturday&apos;s close, trading for that team closes at kickoff
                  and stays closed until the market&apos;s next opening. Orders can still be placed during this time;
                  they just won&apos;t process until the market re-opens.
                </ListItemText>
              </BulletList>
            </ListItemText>
            <ListItemText>
              Since no trades process while a team&apos;s market is closed, its share value is frozen from kickoff until
              the market reopens — that frozen share value is what regular season win dividends are based on (see
              Regular Season Dividends).
            </ListItemText>
            <ListItemText>
              After the last week of the regular season, the market opens one final time, then closes before conference
              championships and does not reopen. Share ownership locks at that close for postseason dividend purposes.
            </ListItemText>
            <ListItemText>
              Orders that build up while the market is closed are resolved together the next time the market opens for
              that team, per Market-Opening Reconciliation below.
            </ListItemText>
          </BulletList>
        </RuleSection>

        <RuleSection title="Preseason Auction">
          <BulletList>
            <ListItemText>
              The season begins with an initial offering before games are played — our league&apos;s IPO — distributing
              each team&apos;s issued shares based on player bids. Shares left unsold remain owned by the bank,
              available for purchase all season at the team&apos;s initial price.
            </ListItemText>
          </BulletList>

          <SubHeading>Auction Format and Timeline</SubHeading>
          <BulletList>
            <ListItemText>
              Players can place bids anytime before the auction; the auction itself begins at 4:00 pm ET on Sunday,
              August 23.
            </ListItemText>
            <ListItemText>
              Teams are processed one at a time, most expensive (by initial price) first, one team per minute starting
              at 4:01 pm ET. A bid counts if it&apos;s placed before its team&apos;s processing instant.
            </ListItemText>
            <ListItemText>
              Players can place new bids, or modify or withdraw existing ones, at any point before their target team is
              processed — letting them react to how the auction unfolds — but not after. Since expensive teams go first,
              winning big early can leave you cash-strapped for later, cheaper teams — an important strategic
              consideration.
            </ListItemText>
            <ListItemText>
              The commissioner may pause the auction if technical issues arise, and if so will announce the pause and
              expected resumption time. While paused, no teams are processed, and the remaining teams will be
              rescheduled in the same order once the auction is resumed.
            </ListItemText>
          </BulletList>

          <SubHeading>Bid Structure</SubHeading>
          <BulletList>
            <ListItemText>Each bid consists of a quantity (number of shares) and a price per share.</ListItemText>
            <ListItemText>Players may place multiple bids on the same team at different price points.</ListItemText>
            <ListItemText>
              The minimum bid price is the team&apos;s initial price. Bids cannot be placed below this price.
            </ListItemText>
          </BulletList>

          <SubHeading>Bid Validation and Processing</SubHeading>
          <BulletList>
            <ListItemText>
              Cash isn&apos;t reserved when a bid is placed, so when a team is processed, each bid is capped to its
              effective quantity: the requested quantity, or floor(available cash ÷ bid price) if lower. A bid with zero
              effective quantity is disqualified from that team&apos;s allocation.
            </ListItemText>
          </BulletList>
          <ExampleCallout>
            <Typography variant="body2">
              A bid for 10 shares at $12 with only $75 in cash is capped to floor($75 ÷ $12) = floor(6.25) = 6 shares.
            </Typography>
          </ExampleCallout>
          <BulletList>
            <ListItemText>
              Players can cancel a bid until it&apos;s processed; once processed, it&apos;s final. Effective bids are
              then ranked by price, highest first, for allocation.
            </ListItemText>
          </BulletList>

          <SubHeading>Share Allocation</SubHeading>
          <BulletList>
            <ListItemText>
              If total demand exceeds supply, the highest-priced bids win shares first, up to the supply available;
              otherwise every bid with a positive effective quantity wins.
            </ListItemText>
            <ListItemText>
              If multiple bids are tied at the same price and not all of them can be fully allocated due to a shortage
              of shares, ties are broken using a round-robin queue. Players are assigned to the queue in randomized
              order at the beginning of the auction. When a tied bid receives shares during tie-breaking, that player is
              moved to the back of the queue. Shares are distributed one at a time across tied bids until all available
              shares are allocated or all tied bids are exhausted.
            </ListItemText>
            <ListItemText>
              <strong>This is a uniform-price auction: every winner pays the same price</strong> — the highest price at
              which a player wanted shares and missed out, or the team&apos;s initial price if there were no unsatisfied
              bids. Unsold shares remain owned by the bank.
            </ListItemText>
            <ListItemText>
              A single player bidding does not count as unfilled demand. If only one player bids, they&apos;ll pay the
              team&apos;s initial price even if their bid was for a higher amount, because there was no competition for
              the shares.
            </ListItemText>
          </BulletList>
          <ExampleCallout>
            <Typography variant="body2">A team offers 5 shares, and three players bid:</Typography>
            <BulletList sx={{ '& > .MuiListItemText-root': { display: 'list-item', mt: 0 } }}>
              <ListItemText>Player 1 bids 3 shares at $20</ListItemText>
              <ListItemText>Player 2 bids 3 shares at $18</ListItemText>
              <ListItemText>Player 3 bids 2 shares at $15</ListItemText>
            </BulletList>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Ranked highest-first, the 5 shares are allocated:
            </Typography>
            <BulletList sx={{ '& > .MuiListItemText-root': { display: 'list-item', mt: 0 } }}>
              <ListItemText>Player 1 wins all 3 shares</ListItemText>
              <ListItemText>Player 2 wins 2 of their 3 shares</ListItemText>
              <ListItemText>Player 3 wins none</ListItemText>
            </BulletList>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Player 2 wanted 3 shares at $18 and got 2, so they missed out on 1 share at $18 — so that&apos;s what
              everyone pays. Player 1 pays $18 even though they bid $20.
            </Typography>
          </ExampleCallout>
        </RuleSection>

        <RuleSection title="Regular Season Trades">
          <BulletList>
            <ListItemText>
              Players may place buy orders (offers) for shares and sell orders (listings) for shares they own.
            </ListItemText>
            <ListItemText>
              A buy order draws on your cash at the moment it matches, so you can have more bids resting than cash to
              fulfill them. If a buy order needs more cash than you have when it matches, the order is limited to the
              affordable quantity, and the unaffordable excess keeps resting in its original place in the queue, filling
              on its own once you have the cash again. The order book shows other players only the portion of a bid its
              owner can currently pay for, and warns you about your own shortfall. A sell order that would have matched
              a bid its owner can&apos;t currently fund falls through to the next-best one.
            </ListItemText>
            <ListItemText>
              <strong>Resting orders are re-checked whenever the market moves.</strong> If a trade, price change, or
              dividend gives you enough cash or headroom to match a trade that previously couldn&apos;t be fulfilled, it
              will trade right then. When two resting orders match this way, the newer one trades at the older
              one&apos;s price.
            </ListItemText>
            <ListItemText>
              Players may cancel any order that has not yet been processed or matched. Once an order is processed and
              executed, it cannot be undone.
            </ListItemText>
            <ListItemText>
              Orders may be partially filled. An order for multiple shares is treated the same as multiple single-share
              orders placed at the same time, so quantity does not affect an order&apos;s matching priority.
            </ListItemText>
            <ListItemText>
              Bank-owned shares act as an automatic sell order for the available bank-owned shares at the team&apos;s
              initial price until the bank supply is exhausted.
            </ListItemText>
            <ListItemText>
              Because the bank always sells at the initial price, player-to-player trades can&apos;t push the share
              value above it until the bank is exhausted. While bank shares remain, a player-to-player trade only
              happens at or below that price — a listing at exactly the initial price beats the bank, and anything
              cheaper beats it outright and can lower the share value.
            </ListItemText>
            <ListItemText>
              <strong>No trade ever executes above twice a team&apos;s current share value.</strong> This limit is there
              so a single transaction can&apos;t move a large amount of cash between two players in one go.
            </ListItemText>
            <ListItemText>
              <strong>
                How far a trade moves a team&apos;s value is limited separately, and that limit tightens the more of the
                team the traders hold.
              </strong>{' '}
              You can pay above it — you just don&apos;t get to revalue the shares you already own by overpaying for one
              more. The team&apos;s value moves according to the trade price, capped at a ceiling of{' '}
              <strong>share value × (200% − combined ownership)</strong>: the two players&apos; positions added
              together, divided by the team&apos;s total stake. A short counts at its size rather than as zero, since
              betting against a team is as much of a stake in its price as owning it. Because shorting creates real new
              shares, a team&apos;s total stake grows with it: issued shares plus twice the shares written short —
              which, once the bank&apos;s shares are gone, is exactly every position on the team added up. So the more
              of a team is shorted, the larger it effectively is, and the smaller any one pair&apos;s share of it. While
              shares are still available at the initial price, both limits are raised to that initial price.
            </ListItemText>
          </BulletList>
          <ExampleCallout>
            <Typography variant="body2">
              A team&apos;s share value is $20, so nobody can trade it above <strong>$40</strong>. Two players who
              together own 5 of its 10 issued shares have a combined ownership of 50%, putting their value limit at $20
              × (200% − 50%) = <strong>$30</strong>. One has a resting buy at $35 and the other lists a sell at $24, so
              they trade at <strong>$35</strong> — the full resting bid, comfortably under the $40 limit. But the
              team&apos;s value only moves as if the trade were at $30, so it goes to $22 rather than $23. (A pair
              owning none of the team would move it the full way; a pair owning all 10 shares couldn&apos;t move it at
              all.)
            </Typography>
          </ExampleCallout>
          <BulletList>
            <ListItemText>
              <strong>No trading a team back and forth with the same player in one market window.</strong> Once you
              trade a team with another player in one direction (you sold to them, or bought from them), you can&apos;t
              reverse that trade with that same player until the next time the market opens. Trading the same direction
              repeatedly is fine. The market-opening reconciliation and the continuous trading window it opens count as
              a single window for this rule.
            </ListItemText>
            <ListItemText>
              <strong>While the market is open, trading is continuous:</strong> when a new order can match an existing
              order, the trade executes against the best existing order, at the existing order&apos;s price. If two
              existing orders have the same price, the order that was placed earlier matches first.
            </ListItemText>
            <ListItemText>
              Orders that accumulate while the market is closed — over the weekend, or after a team&apos;s game kicks
              off — are resolved together the next time the market opens for that team, following the market-opening
              reconciliation process below.
            </ListItemText>
          </BulletList>
        </RuleSection>

        <RuleSection title="Short Selling">
          <BulletList>
            <ListItemText>
              <strong>Shorting writes a new share and sells it.</strong> A short sell order rests in the book like any
              other sell order, and you can place one on any team at any time. A short order can match once you no
              longer own any of that team, and there is a matching buy order from another player. When it matches, the
              buyer receives a real share with full ownership, dividends, and portfolio value, the same as buying from
              another player or from the bank. You take on a negative position: a liability for the quantity you&apos;ve
              written.
            </ListItemText>
            <ListItemText>
              <strong>Covering is a normal buy order.</strong> A buy order for a team you&apos;re short on pays down
              that liability first, and only creates real ownership once the liability is gone. A short order matches
              only while you hold no real shares of that team, so selling out makes a resting short eligible again on
              its own, and it can fill the moment that happens.
            </ListItemText>
            <ListItemText>
              <strong>A short can never itself lower a team&apos;s share value.</strong> The price you set is the
              minimum you&apos;d accept. What other players see, what decides matching priority, and what the trade
              executes at are all one number: the higher of your price and the team&apos;s current share value, checked
              live at the moment of matching. Because that number is always at or above the current value, a short sell
              can only ever hold a team&apos;s value steady or push it up.
            </ListItemText>
            <ListItemText>
              <strong>Real supply fills before a short.</strong> At the same effective price, a real sell order beats
              the bank or a secondary offering, and the bank or secondary offering beats a short. A short fills once
              real sellers, the bank, and secondary shares are exhausted at that price.
            </ListItemText>
            <ListItemText>
              <strong>Shorting a team becomes available once its bank shares sell out</strong>, at any price. You can
              place a short order at any time, and it starts matching automatically as soon as the team&apos;s bank
              supply is gone.
            </ListItemText>
            <ListItemText>
              <strong>Total short exposure is capped at 50% of your current portfolio value</strong>, added up across
              every team you&apos;re short. This is checked live whenever a short order matches — if only part of an
              order fits under your remaining headroom, that part fills and the rest stays open, the same as any other
              partial fill. Headroom shrinks as a short position moves against you and recovers as you cover, so it is
              always measured against your current standing. What you have left shows as <strong>Short Headroom</strong>{' '}
              on the standings and on your portfolio.
            </ListItemText>
            <ListItemText>
              <strong>Shorts are paid when their team loses a regular season game</strong> — see Regular Season
              Dividends for the formula and an example.
            </ListItemText>
            <ListItemText>
              A short position counts toward the value limit (see Regular Season Trades) at its size, the same way a
              real position does: betting against a team is as much of a stake in its price as owning it.
            </ListItemText>
            <ListItemText>
              <strong>Once a short executes, it&apos;s fully attributed.</strong> It shows up with your name on the
              team&apos;s owners list and on your own portfolio page, the same as any real position, since it&apos;s a
              real part of your portfolio value and that value stays transparent to everyone. While a short order is
              still open, other players see its effective price rather than the limit you set.
            </ListItemText>
          </BulletList>
        </RuleSection>

        <RuleSection title="Regular Season Secondary Auctions">
          <BulletList>
            <ListItemText>
              Every team is eligible for a secondary offering each week — a way to issue new shares — processed as part
              of that week&apos;s market-opening reconciliation. Because the offering is always priced at or above the
              bank&apos;s, it is only ever reached once a team&apos;s bank shares are gone.
            </ListItemText>
            <ListItemText>
              Given N league members, the offering&apos;s share limit is N / 10, rounded down, with a minimum of 1.
              Shares that go unissued because of lack of demand don&apos;t carry over to future weeks.
            </ListItemText>
          </BulletList>
          <ExampleCallout>
            <Typography variant="body2">
              A 24-member league can issue 2 new shares, while an 8-member league issues 1 (the minimum).
            </Typography>
          </ExampleCallout>
        </RuleSection>

        <RuleSection title="Market-Opening Reconciliation">
          <BulletList>
            <ListItemText>
              When the market opens for a team, every pending buy and sell order for that team clears together in one
              event, instead of being processed one at a time in the order they were placed. Creation date only breaks
              ties between orders at the same price — it never decides who trades or at what price.
            </ListItemText>
            <ListItemText>
              If the team has bank-owned shares, they count as a sale offer for that quantity at the team&apos;s initial
              price.
            </ListItemText>
            <ListItemText>
              If the team is eligible for a secondary offering this week, its share limit also counts as a sale offer,
              priced at the higher of the team&apos;s initial price and current share value, so new shares never sell
              below the original offering price. This offer is included regardless of remaining bank supply — since
              it&apos;s always priced at or above the bank, it&apos;s only reached after every cheaper offer, including
              the whole bank supply, sells out within this same event (the team doesn&apos;t need to already be
              bank-drained beforehand).
            </ListItemText>
            <ListItemText>
              Sell orders, the bank&apos;s offer, and the secondary offer (if any) are ranked together from cheapest to
              most expensive — real sell orders rank ahead of the bank or secondary at an identical price. Buy orders
              are ranked from most to least expensive.
            </ListItemText>
            <ListItemText>
              The cheapest remaining sale offer is matched against the most expensive remaining buy order, one share at
              a time, for as long as the buy price meets or exceeds the sale price. The price of the last sale offer
              reached this way becomes the execution price for the whole event, unless competition for the bank or
              secondary offer pushes it higher.
            </ListItemText>
            <ListItemText>
              Every trade in the event — including sales from the bank and the secondary offering — happens at this
              single execution price, not each order&apos;s own price. A cheap seller still gets the full execution
              price; an expensive buyer still only pays it.
            </ListItemText>
            <ListItemText>
              The bank and secondary offer can sell above their usual price when there&apos;s real competition, priced
              exactly as the preseason auction is:{' '}
              <strong>the highest price another player wanted shares at and could have paid</strong> — either someone
              who got nothing at all, or a tied group that had to be split. Your own unfilled demand never counts, so a
              lone bidder pays the usual price however much they asked for. Real sell orders never get this markup on
              their asking price, though they still receive the event&apos;s single execution price like everyone else.
            </ListItemText>
            <ListItemText>
              Ties are broken two ways: among sell orders at the same price, the earlier one (by creation date) matches
              first, same as continuous trading. Among buy orders at the same price that together exceed the remaining
              supply, shares split one at a time in round-robin order, using the same rotating queue shared with the
              preseason auction and secondary offering — regardless of whether the shares come from another player, the
              bank, or the secondary offering. A buy order that loses this tie-break gets nothing this event, even
              though its price qualified.
            </ListItemText>
            <ListItemText>
              As in regular trading, a buy order that needs more cash than its owner has takes part in the event only up
              to what they can afford, and whatever is left over stays open for the trading window that follows. Demand
              nobody can pay for never takes shares from another player, and never moves the event&apos;s single price.
            </ListItemText>
            <ListItemText>
              The same flat price limit applies here as in ordinary trading — nothing executes above twice the
              team&apos;s current value (see Regular Season Trades). Because an event settles at one price for everyone,
              it is all-or-nothing: if an event would settle above the limit, nothing trades at all and every order
              stays open.
            </ListItemText>
            <ListItemText>
              A team&apos;s value is recalculated after each trade in the event, but not always from the price that was
              paid. When a trade executes above the ownership ceiling (see Regular Season Trades for calculation), the
              value moves as if it had happened at that ceiling. So two players who own much of a team between them can
              still trade it at up to twice its value, but the value itself will not move as much as a normal trade at
              that price. When another player&apos;s unmet demand pushed the price above the last sale offer, that
              player&apos;s ownership ceiling limits the move as well.
            </ListItemText>
            <ListItemText>
              Within one event, teams are settled one at a time, most expensive first (by current share value).
            </ListItemText>
            <ListItemText>
              Orders left unmatched aren&apos;t canceled; they stay open for ordinary trading once the market is open
              for the week.
            </ListItemText>
          </BulletList>
          <ExampleCallout>
            <Typography variant="body2">
              A team reopens with two sell orders (1 share at $10, 1 at $12) and two buy orders (Player X wants 2 at
              $16, Player Y wants 1 at $11). Matching cheapest sale against highest buy, one share at a time: the $10
              sale fills X&apos;s $16, then the $12 sale fills X&apos;s second $16; the next-cheapest offer has no
              remaining buyer willing to meet its price (only Y&apos;s $11 is left), so matching stops. Two shares
              trade, and the last sale offer reached ($12) sets the single execution price. Both sellers receive $12 —
              even the one who listed at $10 — and X pays $12 for both shares. Y&apos;s $11 bid is too low to trade.
            </Typography>
          </ExampleCallout>
        </RuleSection>

        <RuleSection title="Regular Season Dividends">
          <BulletList>
            <ListItemText>
              When a team wins a regular season game against an FBS opponent, owners of that team immediately receive a
              dividend — 20% of the losing team&apos;s current share value per share owned — added to their cash
              balance. This happens at game completion, before the market reopens for the next week.
            </ListItemText>
            <ListItemText>
              When a team loses a regular season game, players that are short on that team immediately receive a
              dividend — 20% of the losing team&apos;s own current share value per share shorted — added to their cash
              balance. This also happens at game completion, before the market reopens for the next week.
            </ListItemText>
            <ListItemText>
              Only games that legally finish pay dividends. Cancellations, no-contests, and forfeits pay neither team.
              Rescheduled games count for the week they&apos;re actually played. Wins over non-FBS opponents pay the
              team&apos;s owners nothing (a short position is paid for any loss, including to a non-FBS opponent).
            </ListItemText>
          </BulletList>
          <ExampleCallout>
            <Typography variant="body2">
              Team A (share value $60) beats Team B (share value $75). Both dividends are 20% of Team B&apos;s $75,
              since B is the team that lost: owning 3 shares of Team A pays $45.00 (3 × 20% × $75.00), and being short 3
              shares of Team B pays $45.00 as well. One finished game pays both sides, and Team A&apos;s own $60 value
              is not part of the calculation.
            </Typography>
          </ExampleCallout>
        </RuleSection>

        <RuleSection title="Postseason Dividends">
          <BulletList>
            <ListItemText>
              Postseason dividends pay a fixed amount per share owned. A team can earn multiple, cumulative payouts for
              distinct accomplishments.
              <BulletList sx={{ listStyle: 'circle' }}>
                <ListItemText>Power 4 conference champions ($40.00 per share)</ListItemText>
                <ListItemText>Group of 6 conference champions ($20.00 per share)</ListItemText>
                <ListItemText>Non-Playoff bowl game winners ($20.00 per share)</ListItemText>
                <ListItemText>Playoff participants ($25.00 per share)</ListItemText>
                <ListItemText>Playoff top-4 seed (additional $25.00 per share)</ListItemText>
                <ListItemText>Each playoff win (additional $25.00 per share)</ListItemText>
              </BulletList>
            </ListItemText>
            <ListItemText>Dividends are added to the player&apos;s cash balance.</ListItemText>
            <ListItemText>
              <strong>Postseason dividends run both ways.</strong> If you&apos;re short on a team after the final market
              close, you are <strong>charged</strong> the same amount per share that owners are paid — the money comes
              out of your cash balance, and it can take you below zero. Unlike the regular season, where a short is paid
              when its team loses and neither side is ever charged a penalty, the postseason is symmetric and includes
              negative dividends for shorts on teams with postseason accomplishments.
            </ListItemText>
            <ListItemText>
              This only matters for a short you still hold at the final market close. If you cover a short before
              trading ends, you will not be charged the postseason dividend. A short carried into the postseason is a
              real bet that the team won&apos;t accomplish anything, but covering that short before the final market
              close removes that liability.
            </ListItemText>
          </BulletList>
          <ExampleCallout>
            <Typography variant="body2">
              Own 3 shares of a Power 4 champion that also reaches the playoff as a top-4 seed and wins two playoff
              games. Per share that&apos;s $40 (champion) + $25 (playoff participant) + $25 (top-4 seed) + $25 + $25
              (two playoff wins) = $140 per share, or $420 across all 3 shares.
            </Typography>
          </ExampleCallout>
          <ExampleCallout>
            <Typography variant="body2">
              A player that is short 3 shares of that same team would be charged the same $140 per share, or $420 across
              all 3 shares, even if it brings their cash balance below zero.
            </Typography>
          </ExampleCallout>
        </RuleSection>

        <RuleSection title="Payout Structure">
          <BulletList>
            <ListItemText>
              At season&apos;s end, players above the league-average portfolio value split the payout pool (total
              buy-ins) proportionally to how far above average they are. Portfolio value is cash balance plus the share
              value of every share owned.
            </ListItemText>
          </BulletList>
          <ExampleCallout>
            <Typography variant="body2">
              Five players buy in for $20 each and get $2000 each in play money, so the pool is $100. Final portfolio
              values:
            </Typography>
            <BulletList sx={{ listStyle: 'circle', '& > .MuiListItemText-root': { display: 'list-item', mt: 0 } }}>
              <ListItemText>$3000</ListItemText>
              <ListItemText>$2750</ListItemText>
              <ListItemText>$2500</ListItemText>
              <ListItemText>$2250</ListItemText>
              <ListItemText>$2000</ListItemText>
            </BulletList>
            <Typography variant="body2" sx={{ mt: 1 }}>
              The league average is $2500. Amounts <strong>above average</strong> are $500, $250, $0, $0, and $0. Player
              A is $500 above average out of $750 total above average, so A takes 66.67% of the pool ($66.67); Player B
              takes 33.33% ($33.33). Everyone at or below average gets nothing.
            </Typography>
          </ExampleCallout>
          <BulletList>
            <ListItemText>
              Notice that payouts <strong>do not</strong> correlate directly to increases in a player&apos;s portfolio
              value. If you increase your portfolio value by 20%, but everybody else increases theirs by an average of
              40%, you will lose money even though your portfolio gained 20%.
            </ListItemText>
            <ListItemText>
              A negative portfolio value counts as $0 for this calculation — a player who ends up underwater contributes
              $0 to the league average, not their true negative number. (Elsewhere on the site, your portfolio value is
              always shown as its true, possibly negative figure — this floor applies only here, to the final payout
              math.)
            </ListItemText>
          </BulletList>
          <ExampleCallout>
            <Typography variant="body2">
              Same five players as above, but Player E&apos;s portfolio value is actually −$1000, not $2000. For this
              calculation E contributes $0, not −$1000: the league average becomes ($3000 + $2750 + $2500 + $2250 + $0)
              / 5 = $2100. Amounts above average are $900, $650, $400, $150, and $0 — Player A now takes 42.86% of the
              pool ($42.86), down from 66.67%, since the average dropped and more players cleared it.
            </Typography>
          </ExampleCallout>
        </RuleSection>

        <RuleSection title="Sportsmanship and Game Integrity">
          <BulletList>
            <ListItemText>
              This game is intended to be competitive, but also fun. Players are expected to act in good faith and
              within both the letter and spirit of these rules.
            </ListItemText>
            <ListItemText>
              Collusion, deliberate market manipulation, sham transactions, and other conduct intended to exploit
              loopholes or undermine the integrity of the game are prohibited.
            </ListItemText>
            <ListItemText>
              The commissioner has sole discretion to interpret these rules, determine player intent, and resolve
              situations not explicitly covered by the rules.
            </ListItemText>
            <ListItemText>
              To protect the integrity of the league, the commissioner may take any action reasonably necessary,
              including:
              <MuiList
                dense
                disablePadding
                sx={{ listStyle: 'decimal', pl: 4, '& > .MuiListItemText-root': { display: 'list-item', mt: 0.75 } }}
              >
                <ListItemText>Pausing or temporarily closing the market.</ListItemText>
                <ListItemText>Canceling, modifying, or reversing transactions.</ListItemText>
                <ListItemText>
                  Adjusting cash balances, share ownership, team share values, or portfolio values.
                </ListItemText>
                <ListItemText>Disqualifying players from participation.</ListItemText>
                <ListItemText>Reducing or withholding payouts.</ListItemText>
              </MuiList>
            </ListItemText>
            <ListItemText>
              Commissioner decisions regarding game administration, rule interpretation, and disciplinary actions are
              final.
            </ListItemText>
            <ListItemText>
              Players should remember that the purpose of this game is to make the college football season more
              enjoyable. Winning is encouraged; finding technicalities that damage the experience for everyone else is
              not.
            </ListItemText>
          </BulletList>
        </RuleSection>
      </Box>
    </StickyTabBarContext.Provider>
  );
}
